import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import FolderItem from "./FolderItem";
import { useDatabase } from "./DatabaseContext";
import CustomMetadataManagementModal from "../modals/CustomMetadataManagementModal";
import QueueStatus from "./QueueStatus";
import { getAdminInfo } from "../../api";
import { useSortableSensors } from "../../hooks/useSortableSensors";
import { User } from "../../types/types";
import Icon from "../common/Icon";

const DatabaseSidebar: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const {
        folders,
        selectedFolderId,
        handleEditFolder,
        handleDeleteFolder,
        handleSelectFolder,
        addFolder,
        reorderFolders,
    } = useDatabase();
    const sensors = useSortableSensors();

    const [isAddingFolder, setIsAddingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
    const [user, setUser] = useState<User | null>(null);

    const handleAddFolderClick = () => {
        setIsAddingFolder(true);
    };

    const handleAddFolderSubmit = () => {
        if (newFolderName.trim()) {
            addFolder(newFolderName.trim());
            setNewFolderName("");
        }
        setIsAddingFolder(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleAddFolderSubmit();
        } else if (e.key === "Escape") {
            setIsAddingFolder(false);
            setNewFolderName("");
        }
    };

    const handleManageMetadata = () => {
        setIsMetadataModalOpen(true);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = folders.findIndex((folder) => folder.id === active.id);
        const newIndex = folders.findIndex((folder) => folder.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(folders, oldIndex, newIndex);
        reorderFolders(reordered.map((folder) => folder.id));
    };

    const handleCloseMetadataModal = () => {
        setIsMetadataModalOpen(false);
    };

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) return;

                const userData = await getAdminInfo({ token });
                setUser(userData);
            } catch (error) {
                console.error("Error fetching user info:", error);
            }
        };

        fetchUserInfo();
    }, []);

    return (
        <aside className="dbSidebar">
            <div className="dbSideTitle">{t("database")}</div>

            {user?.cmpany?.id && (
                <div className="dbSideStatus">
                    <Icon name="ic-clock" />
                    <span className="dbSideStatusLabel">{t("processingQueue") || "File d'attente"}</span>
                    <QueueStatus companyId={user.cmpany.id} compact />
                </div>
            )}

            <div className="dbSideMeta" onClick={handleManageMetadata} role="button" tabIndex={0}>
                <Icon name="ic-settings" />
                {t("manageCustomMetadata")}
            </div>

            <div className="dbFolderHead">{t("filterByFolder") || t("knowledgeLibrary")}</div>

            {!isAddingFolder ? (
                <button type="button" className="btn btn-primary dbNewFolder" onClick={handleAddFolderClick}>
                    <Icon name="ic-plus" />
                    {t("newFolder")}
                </button>
            ) : (
                <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onBlur={handleAddFolderSubmit}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    placeholder={t("folderName")}
                    className="new-folder-input"
                />
            )}

            <ul className="dbFolderList">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={folders.map((folder) => folder.id)} strategy={verticalListSortingStrategy}>
                        {folders.map((folder) => (
                            <FolderItem
                                key={folder.id}
                                id={folder.id}
                                name={folder.name}
                                onEdit={handleEditFolder}
                                onDelete={handleDeleteFolder}
                                onSelect={() => handleSelectFolder(folder.id)}
                                isSelected={folder.id === selectedFolderId}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </ul>

            <CustomMetadataManagementModal isOpen={isMetadataModalOpen} onClose={handleCloseMetadataModal} />
        </aside>
    );
};

export default DatabaseSidebar;
